"use client";

import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/app/components/ui/form";
import { Input } from "@/app/components/ui/input";
import Icon from "../../Icon";
import DynamicPhoneInput from "../../../en/contact/DynamicPhoneInput";
import { ContactFormData } from "../../../lib/forms/contact-schema";

type ContactInformationSectionProps = {
  control: Control<ContactFormData>;
  preferredContactMethod: "email" | "phone";
};

export function ContactInformationSection({ control, preferredContactMethod }: ContactInformationSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 className="text-base md:text-lg font-normal text-ui-card mb-4 flex items-center gap-2">
        <Icon name="user" size="md" className="text-ui-card/80" />
        {t("Contact Information")}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-normal text-ui-card/90">
                {t("Name")} <span className="text-red-500" aria-label={t("Required")}>*</span>
              </FormLabel>
              <FormDescription className="sr-only">
                {t("Enter your full name")}
              </FormDescription>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("Your full name")}
                  aria-required="true"
                  autoComplete="name"
                  className="rounded-none border-ui-border bg-ui-page text-ui-card placeholder:text-ui-card/40 focus:ring-brand-accent focus:border-transparent"
                />
              </FormControl>
              <FormMessage className="text-xs text-red-600" />
            </FormItem>
          )}
        />

        {/* Email */}
        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-normal text-ui-card/90">
                {t("E-mail")} <span className="text-red-500" aria-label={t("Required")}>*</span>
              </FormLabel>
              <FormDescription className="sr-only">
                {t("Enter your email address")}
              </FormDescription>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder={t("your@email.com")}
                  aria-required="true"
                  autoComplete="email"
                  className="rounded-none border-ui-border bg-ui-page text-ui-card placeholder:text-ui-card/40 focus:ring-brand-accent focus:border-transparent"
                />
              </FormControl>
              <FormMessage className="text-xs text-red-600" />
            </FormItem>
          )}
        />

        {/* Phone */}
        <FormField
          control={control}
          name="phone"
          render={({ field, fieldState }) => (
            <FormItem className="md:col-span-2 md:col-start-1">
              <FormLabel className="text-sm font-normal text-ui-card/90">
                {t("Phone")} {preferredContactMethod === "phone" ? (
                  <span className="text-red-500" aria-label={t("Required")}>*</span>
                ) : (
                  <span className="text-ui-card/70 text-xs">({t("Optional")})</span>
                )}
              </FormLabel>
              <FormDescription className="text-xs text-ui-card/60 mt-1">
                {t("Include country code for international numbers")}
              </FormDescription>
              <FormControl>
                <div className={fieldState.error ? "border-red-500" : ""}>
                  <DynamicPhoneInput
                    value={field.value || ""}
                    onChange={(value) => field.onChange(value || "")}
                    onBlur={field.onBlur}
                    placeholder={t("+30 123 456 7890")}
                    aria-required={preferredContactMethod === "phone"}
                    className={`w-full [&_.PhoneInputInput]:px-4 [&_.PhoneInputInput]:py-3 [&_.PhoneInputInput]:text-sm [&_.PhoneInputInput]:font-light [&_.PhoneInputInput]:text-ui-card [&_.PhoneInputInput]:bg-ui-page [&_.PhoneInputInput]:border [&_.PhoneInputInput]:rounded-none [&_.PhoneInputInput]:focus:outline-none [&_.PhoneInputInput]:focus:ring-2 [&_.PhoneInputInput]:focus:ring-brand-accent [&_.PhoneInputInput]:focus:border-transparent [&_.PhoneInputInput]:transition-colors [&_.PhoneInputInput]:placeholder:text-ui-card/40 ${
                      fieldState.error
                        ? "[&_.PhoneInputInput]:border-red-500"
                        : "[&_.PhoneInputInput]:border-ui-border"
                    } [&_.PhoneInputCountry]:border-ui-border [&_.PhoneInputCountry]:shrink-0 [&_.PhoneInputCountrySelect]:bg-ui-page [&_.PhoneInputCountrySelect]:text-ui-card [&_.PhoneInputCountryIcon]:mr-2 [&_.PhoneInputCountrySelectArrow]:hidden [&_.PhoneInputCountrySelect]:text-[0] [&_.PhoneInputCountrySelect]:w-14 [&_.PhoneInputCountrySelect]:min-w-14 [&_.PhoneInputCountrySelect]:max-w-14 [&_.PhoneInputCountrySelect]:shrink-0`}
                    style={{
                      '--PhoneInputCountrySelectArrow': 'none',
                    } as React.CSSProperties}
                    touched={!!fieldState.isTouched}
                    error={!!fieldState.error}
                  />
                </div>
              </FormControl>
              <FormMessage className="text-xs text-red-600" />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

